import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Config } from "../../utils/config";
import MenuItem from "./MenuItem";
import BuyOneGetOnePopupMenu from "./BuyOneGetOneMenu";
import MealBundlePopupMenu from "./MealBundleMenu";
import ErrorBoundary from "../../ErrorBoundary";

export default function PromoPopupMenu({
  isOpen,
  setOpen,
  menuList,
  selectedPromo,
  restaurantDetail,
  orderingMethod,
  sizesOrderingMethod,
  selectedType,
  isDineIn,
  cartId,
  id,
  authToken,
  setCartTotal,
  setSelectedType,
  //   isMenuItemTiming,
}) {
  const { t } = useTranslation();
  const [isItemOpen, setItemOpen] = React.useState(false);
  const [updateData, setUpdateData] = React.useState(null);

  const eligibleItems = selectedPromo.details.eligible_items || [];
  const eligibleItemIds: string[] = [];
  const eligibleItemSizes: { itemId: string; sizes: string[] }[] = [];
  const eligibleSizesMap = new Map();

  if (!selectedPromo) return null;
  if (selectedPromo.type === "buy_one_get_one")
    return (
      <BuyOneGetOnePopupMenu
        isOpen={isOpen}
        setOpen={setOpen}
        menuList={menuList}
        selectedPromo={selectedPromo}
        restaurantDetail={restaurantDetail}
        orderingMethod={orderingMethod}
        sizesOrderingMethod={sizesOrderingMethod}
        selectedType={selectedType}
        isDineIn={isDineIn}
        cartId={cartId}
        id={id}
        authToken={authToken}
        setCartTotal={setCartTotal}
        setSelectedType={setSelectedType}
      />
    );
  if (selectedPromo.type === "meal_bundle")
    return (
      <MealBundlePopupMenu
        isOpen={isOpen}
        setOpen={setOpen}
        menuList={menuList}
        selectedPromo={selectedPromo}
        restaurantDetail={restaurantDetail}
        orderingMethod={orderingMethod}
        sizesOrderingMethod={sizesOrderingMethod}
        selectedType={selectedType}
        isDineIn={isDineIn}
        cartId={cartId}
        id={id}
        authToken={authToken}
        setCartTotal={setCartTotal}
        setSelectedType={setSelectedType}
      />
    );

  eligibleItems.forEach((category) => {
    category.items.forEach((item: any) => {
      let isItemEligible = item.checked;
      let hasSizesEligible = false;
      let eligibleSizes: string[] = [];

      if (item.sizes && item.sizes.length > 0) {
        item.sizes.forEach((size) => {
          if (size.checked) {
            hasSizesEligible = true;
            eligibleSizes.push(size.id);
          }
        });
      }

      const isFinallyEligible = isItemEligible || hasSizesEligible;

      if (isFinallyEligible) {
        eligibleItemIds.push(item.id);

        if (eligibleSizes.length > 0) {
          eligibleItemSizes.push({
            itemId: item.id,
            sizes: eligibleSizes,
          });

          eligibleSizesMap.set(item.id, eligibleSizes);
        }
      }
    });
  });

  const filteredContainers = menuList.containerOrder
    .filter((containerId) => {
      const container = menuList.containers[containerId.id];

      if (!container) {
        return false;
      }

      if (container.visibility === 0) {
        return false;
      }

      const hasEligibleItems = container.items.some((menu) => {
        if (menu.visibility === 0) {
          return false;
        }

        if (menu[orderingMethod] === "0.00") {
          return false;
        }

        if (menu.availability !== "in_stock") {
          return false;
        }

        const isEligible = eligibleItemIds.includes(menu.id);
        if (isEligible && eligibleSizesMap.has(menu.id)) {
        }

        return isEligible;
      });
      return hasEligibleItems;
    })
    .map((containerId) => {
      const container = menuList.containers[containerId.id];
      return container;
    });

  const handleItemClick = (menu) => {
    if (menu.availability === "in_stock" && menu.is_available) {
      const menuWithPromoInfo = {
        ...menu,
        promoDiscount: selectedPromo.details.discount,
        promoId: selectedPromo.id,
      };

      if (eligibleSizesMap.has(menu.id)) {
        menuWithPromoInfo.eligibleSizes = eligibleSizesMap.get(menu.id);
      }

      setUpdateData(menuWithPromoInfo);
      setItemOpen(true);
    }
  };

  return (
    <>
      <ErrorBoundary>
        <Dialog open={isOpen} onOpenChange={(isOpen) => setOpen(isOpen)}>
          <DialogContent className="p-0 rounded-lg sm:rounded-[20px] overflow-y-auto max-h-[75vh]">
            <DialogHeader>
              <div className="px-4 pt-5 pb-4 bg-green">
                <DialogTitle className="flex items-center justify-between">
                  <span>{selectedPromo.name}</span>
                </DialogTitle>
                <DialogDescription>
                  {selectedPromo.description}
                </DialogDescription>
              </div>
            </DialogHeader>

            <div className="p-4 max-h-full overflow-y-auto custom-scrollbar my-2">
              {filteredContainers.length === 0 ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex justify-center">
                      <Label>{t("no_eligible_items_found")}</Label>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {filteredContainers.map((container, index) => {
                    if (container.visibility === 0) {
                      return null;
                    }

                    const filteredItems = container.items.filter((menu) => {
                      if (menu.visibility === 0) {
                        return false;
                      }

                      if (menu[orderingMethod] === null) {
                        if (menu?.sizes[0][sizesOrderingMethod] === "0.00") {
                          return false;
                        }
                      }

                      if (menu[orderingMethod] === "0.00") {
                        return false;
                      }

                      if (menu.availability !== "in_stock") {
                        return false;
                      }

                      const isEligible = eligibleItemIds.includes(menu.id);

                      if (isEligible && eligibleSizesMap.has(menu.id)) {
                      }

                      return isEligible;
                    });

                    if (filteredItems.length === 0) return null;

                    return (
                      <div className="space-y-3" key={index}>
                        <div className="sticky top-0 bg-white z-10 py-2 flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-primary">
                            {container?.title}
                          </h3>
                          {/* <Badge className="bg-primary text-white text-xs">
                          {selectedPromo.details.discount} {t("off")}
                        </Badge> */}
                        </div>

                        <div className="grid gap-2">
                          {filteredItems.map((menu, itemIndex) => {
                            const isMenuAvailable = menu.is_available;
                            const originalPrice = isDineIn
                              ? menu?.dineInPrice === null
                                ? parseFloat(
                                    menu?.sizes[0].dine_in_price
                                  ).toFixed(2)
                                : parseFloat(menu?.dineInPrice).toFixed(2)
                              : menu[orderingMethod] === null
                              ? parseFloat(
                                  menu?.sizes[0][sizesOrderingMethod]
                                ).toFixed(2)
                              : parseFloat(menu[orderingMethod]).toFixed(2);

                            const hasSizeRestrictions = eligibleSizesMap.has(
                              menu.id
                            );

                            return (
                              <Card
                                key={itemIndex}
                                className={`p-3 hover:shadow-md transition-shadow ${
                                  menu.availability === "in_stock" &&
                                  isMenuAvailable
                                    ? "cursor-pointer"
                                    : "cursor-not-allowed opacity-70"
                                }`}
                                onClick={() => handleItemClick(menu)}
                              >
                                <div className="flex gap-3">
                                  {menu.image && (
                                    <div className="flex-shrink-0 w-16 h-16">
                                      <img
                                        src={`${
                                          Config.env.app.image_url
                                        }/${menu.image.replace("//", "/")}`}
                                        alt={menu.name}
                                        className="h-full w-full rounded-md object-cover"
                                      />
                                    </div>
                                  )}

                                  <div className="flex flex-col justify-between flex-grow">
                                    <div>
                                      <div className="flex items-center justify-between gap-1 mb-1">
                                        <h4 className="font-semibold text-sm line-clamp-1">
                                          {menu?.name?.toString()}
                                        </h4>
                                        {/* {hasSizeRestrictions && (
                                        <Badge
                                          variant="outline"
                                          className="text-xs"
                                        >
                                          {t("coupon.menu.selected_sizes_only")}
                                        </Badge>
                                      )} */}
                                      </div>

                                      <p className="text-xs text-gray-500 line-clamp-1 mt-1">
                                        {menu?.itemDescription?.toString()}
                                      </p>
                                    </div>

                                    <div className="flex items-baseline gap-2 mt-1">
                                      <p className="text-xs text-gray-500 font-bold">
                                        {selectedType !== "" &&
                                          selectedPromo.type !==
                                            "meal_bundle" &&
                                          `${restaurantDetail?.currencyDetails?.symbol} ${originalPrice}`}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <MenuItem
          isOpen={isItemOpen}
          setOpen={() => setItemOpen(!isItemOpen)}
          updateData={updateData}
          cartId={cartId}
          id={id}
          authToken={authToken}
          setCartTotal={setCartTotal}
          restaurantDetail={restaurantDetail}
          isDineIn={isDineIn}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          orderingMethod={orderingMethod}
          sizesOrderingMethod={sizesOrderingMethod}
          couponValue={selectedPromo.coupon}
        />
      </ErrorBoundary>
    </>
  );
}
